import React, { useState, useCallback, useEffect, useRef } from 'react';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../../../components/Common/Notification';
import Button from '../../Common/Button';
import TextArea from '../../Common/TextArea';
import { useBulkLocationPushdownSearch, useBulkLocationPushdownSearchNoProject } from '../../../apis/bulkPushdown';
import { IdName } from '../../../apis/bulkPushdown/bulkPushdown.types';
import isEqual from '../../../utils/isEqual';
import './LocationNameForm.scss';

interface LocationNameSearchParamsState {
	term: string;
	isNameSearch: boolean;
}

const INITIAL_SEARCH_PARAMS: LocationNameSearchParamsState = { term: '', isNameSearch: false };

interface LocationNameFormProps {
	setSelectedLocations: (locations: IdName[]) => void;
	selectedLocations: IdName[];
	projectId?: string;
	baselineId: string;
	baselineTaskIds: number[];
	isProjectsActive: boolean;
	isProjectIdRequiredForLocationTask: boolean;
}

const LocationNameForm: React.FC<LocationNameFormProps> = ({
	setSelectedLocations,
	selectedLocations,
	projectId,
	baselineId,
	baselineTaskIds,
	isProjectsActive,
	isProjectIdRequiredForLocationTask,
}) => {
	const [searchParams, setSearchParams] = useState<LocationNameSearchParamsState>(INITIAL_SEARCH_PARAMS);
	const [submittedParams, setSubmittedParams] = useState<LocationNameSearchParamsState>(INITIAL_SEARCH_PARAMS);
	const [hasSearched, setHasSearched] = useState<boolean>(false);
	const [selectedResults, setSelectedResults] = useState<IdName[]>([]);
	const [selectedToReceive, setSelectedToReceive] = useState<IdName[]>([]);
	const isFirstRender = useRef(true);
	const applyResultsRef = useRef(false);

	const shouldUseNoProjectSearch = isProjectsActive === true && isProjectIdRequiredForLocationTask === false && !projectId;

	const byProjectQuery = useBulkLocationPushdownSearch(
		{ ...submittedParams, projectId, baselineId },
		{ enabled: hasSearched && !shouldUseNoProjectSearch }
	);
	const noProjectQuery = useBulkLocationPushdownSearchNoProject(
		{ ...submittedParams, baselineId, baselineTaskIdList: baselineTaskIds },
		{ enabled: hasSearched && shouldUseNoProjectSearch }
	);

	const activeQuery = shouldUseNoProjectSearch ? noProjectQuery : byProjectQuery;
	const searchResults = activeQuery.data?.locations ?? [];

	const refetchActiveQuery = activeQuery.refetch;

	const searchLocations = useCallback(() => {
		// Re-submitting identical criteria leaves the query key unchanged, so nothing would refetch.
		// Ask for a fresh result explicitly so Search is never a no-op. Structural sharing can hand
		// back the same data reference, which would never re-run the effect below - so apply the
		// result here rather than arming applyResultsRef and leaving it armed forever.
		if (hasSearched && isEqual(submittedParams, searchParams)) {
			refetchActiveQuery().then((result) => {
				const payload = result.data;
				if (!payload || payload.isError) return;
				if (shouldUseNoProjectSearch) {
					setSelectedLocations(payload.locations.slice());
				}
			});
			return;
		}
		// New criteria: a new query key guarantees fresh data, so let the effect below apply it.
		applyResultsRef.current = true;
		setSubmittedParams(searchParams);
		setHasSearched(true);
		// setSelectedLocations identity is not stable across renders in the parent
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams, submittedParams, hasSearched, shouldUseNoProjectSearch, refetchActiveQuery]);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		if (projectId) {
			searchLocations();
		}
		// Only re-run the search when projectId changes. Note this does NOT match the deleted JS,
		// whose componentDidUpdate read `this.searchLocations;` without calling it - a dead
		// statement, so changing the project never re-searched there. LocationAttributesForm.js
		// called it properly, so this matches that sibling's intended behavior.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectId]);

	useEffect(() => {
		if (!activeQuery.error) return;
		// A failed search produces no data, so disarm the latch rather than leaving it to fire on
		// the next background refetch and clobber the user's selection.
		applyResultsRef.current = false;
		Notification('error', 'An Error Occurred', activeQuery.error.message);
	}, [activeQuery.error]);

	useEffect(() => {
		if (!activeQuery.data) return;
		const wasExplicitSearch = applyResultsRef.current;
		applyResultsRef.current = false;
		if (activeQuery.data.isError) {
			Notification('error', 'An Error Occurred', activeQuery.data.errors?.[0]?.message ?? 'Failed to search locations');
			return;
		}
		// Only auto-select for the no-project branch, matching the previous behavior, and only when
		// this data came from an explicit search: a window-refocus refetch must not silently
		// re-add locations the user has pruned from the receive list.
		if (shouldUseNoProjectSearch && wasExplicitSearch) {
			setSelectedLocations(activeQuery.data.locations.slice());
		}
		// setSelectedLocations identity is not stable across renders in the parent
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeQuery.data]);

	const onChangeSearchBy = useCallback(() => {
		setSearchParams((prev) => ({ ...prev, isNameSearch: !prev.isNameSearch }));
	}, []);

	const onTermChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setSearchParams((prev) => ({ ...prev, term: e.target.value }));
	}, []);

	const onSelectSearchResult = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
		const selected = Array.from(e.target.selectedOptions)
			.map((option) => searchResults.find((x) => x.id.toString() === option.value))
			.filter((x): x is IdName => !!x);
		setSelectedResults(selected);
	}, [searchResults]);

	const onSelectToReceivePushdown = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
		// This list renders selectedLocations, not searchResults — resolve against what is rendered
		// so entries added by an earlier search can still be removed.
		const selected = Array.from(e.target.selectedOptions)
			.map((option) => selectedLocations.find((x) => x.id.toString() === option.value))
			.filter((x): x is IdName => !!x);
		setSelectedToReceive(selected);
	}, [selectedLocations]);

	const onAllDown = useCallback(() => {
		setSelectedLocations(searchResults.slice());
	}, [searchResults, setSelectedLocations]);

	const onAllUp = useCallback(() => {
		setSelectedLocations([]);
	}, [setSelectedLocations]);

	const onSomeDown = useCallback(() => {
		const toReceivePushdown = selectedLocations.slice();
		selectedResults.forEach((option) => {
			const item = selectedLocations.find((x) => x.id === option.id);
			if (!item) {
				toReceivePushdown.push({ id: option.id, name: option.name });
			}
		});
		setSelectedLocations(toReceivePushdown);
	}, [selectedLocations, selectedResults, setSelectedLocations]);

	const onSomeUp = useCallback(() => {
		const toReceivePushdown = selectedLocations.slice();
		selectedToReceive.forEach((option) => {
			const index = toReceivePushdown.findIndex((x) => x.id === option.id);
			if (index >= 0) {
				toReceivePushdown.splice(index, 1);
			}
		});
		setSelectedLocations(toReceivePushdown);
	}, [selectedLocations, selectedToReceive, setSelectedLocations]);

	const searchByText = searchParams.isNameSearch ? 'Search by Abbreviations' : 'Search By Name';

	return (
		<div>
			<div className="row">
				<div className="col-12 text-end">
					<button type="button" className="btn btn-link" onClick={onChangeSearchBy}>{searchByText}</button>
				</div>
				<div className="col-12">
					{searchParams.isNameSearch ?
						<TextArea rows={5} maxLength={1000} name="term" label="Location Names Start With" type="text" placeholder="Hotel Alpha, Hotel Beta" value={searchParams.term} onChange={onTermChange} />
						:
						<TextArea rows={5} maxLength={1000} name="term" label="Location Abbreviations" type="text" placeholder="ABC, XYZ" value={searchParams.term} onChange={onTermChange} />
					}
				</div>
				<div className="col-12 text-end">
					<div className="input-subtitle">Separate multiple values with comma</div>
				</div>
			</div>
			<div>
				<div className="col-12 text-end">
					<div className="search-button"><Button name="option2-search" text="Search" bootstrapClass="primary" type="button" onClick={searchLocations} /></div>
				</div>
			</div>
			<LoadingIndicator isLoading={activeQuery.isFetching}>
				<div className="row">
					<div className="col-12">
						<span className="has-float-label">
							<select id="searchResultsList" className="form-control mt-3" multiple size={8} defaultValue={[]} onChange={onSelectSearchResult}>
								{searchResults.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
							</select>
							<p className="search-results-count">{searchResults.length} results</p>
							<label htmlFor="searchResultsList">Search Results</label>
						</span>
					</div>
				</div>
				<br />
				<div className="row">
					<div className="col-12 text-center">
						<Button name="angle-double-down" text="" bootstrapClass="secondary me-4" type="button" onClick={onAllDown} iconName="angle-double-down" />
						<Button name="angle-down" text="" bootstrapClass="secondary me-4" type="button" onClick={onSomeDown} iconName="angle-down" />
						<Button name="angle-up" text="" bootstrapClass="secondary me-4" type="button" onClick={onSomeUp} iconName="angle-up" />
						<Button name="angle-double-up" text="" bootstrapClass="secondary me-4" type="button" onClick={onAllUp} iconName="angle-double-up" />
					</div>
				</div>
				<div className="row">
					<div className="col-12">
						<span className="has-float-label">
							<select id="toReceivePushdownList" className="form-control mt-3" multiple size={8} onChange={onSelectToReceivePushdown}>
								{selectedLocations.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
							</select>
							<label htmlFor="toReceivePushdownList">Locations to Receive Tasks</label>
						</span>
					</div>
				</div>
			</LoadingIndicator>
		</div>
	);
};

export default LocationNameForm;
